import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardTitle, Row, Col, Table, Badge, Button } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../api/Axios';

const InspectorDashboard = () => {
  const navigate = useNavigate();
  const { userEmail, token } = useAuth();
  const [stats, setStats] = useState({
    pendingInspections: 0,
    passedProperties: 0,
    scheduledInspections: 0,
    totalProperties: 0
  });

  // Mock data for pending inspections
  const [pendingInspections, setPendingInspections] = useState([
    {
      id: 1,
      propertyName: "Sunrise Student Residence",
      address: "123 University Ave, Cape Town",
      requestDate: "2025-12-10",
      priority: "High",
      status: "Pending"
    },
    {
      id: 2,
      propertyName: "Campus View Apartments",
      address: "45 College Road, Johannesburg",
      requestDate: "2025-12-09",
      priority: "Medium",
      status: "Pending"
    },
    {
      id: 3,
      propertyName: "Student Haven",
      address: "78 Education Street, Durban",
      requestDate: "2025-12-08",
      priority: "High",
      status: "Scheduled"
    }
  ]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const headers = { Authorization: `Token ${token}` };
      
      // Fetch all properties and count passed ones
      const propertiesResponse = await axiosInstance.get('/api/properties/all-created/', { headers });
      const allProperties = Array.isArray(propertiesResponse.data.properties) 
        ? propertiesResponse.data.properties 
        : [];
      const passedCount = allProperties.filter(p => p.inspection_status === 'inspection_passed').length;
      
      setStats({
        pendingInspections: 5,
        passedProperties: passedCount,
        scheduledInspections: 3,
        totalProperties: allProperties.length
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
      // Keep default values on error
      setStats({
        pendingInspections: 0,
        passedProperties: 0,
        scheduledInspections: 0,
        totalProperties: 0
      });
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      High: 'danger',
      Medium: 'warning',
      Low: 'info'
    };
    return <Badge color={colors[priority] || 'secondary'}>{priority}</Badge>;
  };

  const getStatusBadge = (status) => {
    const colors = {
      Pending: 'warning',
      Scheduled: 'info',
      Completed: 'success',
      Cancelled: 'secondary'
    };
    return <Badge color={colors[status] || 'secondary'}>{status}</Badge>;
  };

  return (
    <>
      {/* Welcome Section */}
      <Card className="mb-4">
        <CardBody>
          <CardTitle tag="h4" className="mb-3">
            <i className="bi bi-speedometer2 me-2"></i>
            Inspector Dashboard
          </CardTitle>
          <p className="text-muted">
            Welcome back, Inspector! Here's an overview of your inspection activities.
          </p>
          <small className="text-muted">Logged in as: {userEmail}</small>
        </CardBody>
      </Card>

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col lg="3" md="6" className="mb-3">
          <Card className="border-start border-danger border-4">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h6 className="text-muted mb-1">Pending Inspections</h6>
                  <h2 className="mb-0">{stats.pendingInspections}</h2>
                </div>
                <div className="text-danger" style={{ fontSize: '2.5rem' }}>
                  <i className="bi bi-clipboard-check"></i>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg="3" md="6" className="mb-3">
          <Card 
            className="border-start border-success border-4" 
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/inspector-dashboard/passed-properties')}
          >
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h6 className="text-muted mb-1">Passed Properties</h6>
                  <h2 className="mb-0">{stats.passedProperties}</h2>
                </div>
                <div className="text-success" style={{ fontSize: '2.5rem' }}>
                  <i className="bi bi-check-circle"></i>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg="3" md="6" className="mb-3">
          <Card className="border-start border-info border-4">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h6 className="text-muted mb-1">Scheduled</h6>
                  <h2 className="mb-0">{stats.scheduledInspections}</h2>
                </div>
                <div className="text-info" style={{ fontSize: '2.5rem' }}>
                  <i className="bi bi-calendar3"></i>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg="3" md="6" className="mb-3">
          <Card className="border-start border-primary border-4">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h6 className="text-muted mb-1">Total Properties</h6>
                  <h2 className="mb-0">{stats.totalProperties}</h2>
                </div>
                <div className="text-primary" style={{ fontSize: '2.5rem' }}>
                  <i className="bi bi-building"></i>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Pending Inspections Table */}
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <CardTitle tag="h5" className="mb-0">
              <i className="bi bi-list-check me-2"></i>
              Recent Inspection Requests
            </CardTitle>
            <Button
              color="primary"
              size="sm"
              onClick={() => navigate('/inspector-dashboard/pending-inspections')}
            >
              View All
            </Button>
          </div>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Property Name</th>
                <th>Address</th>
                <th>Request Date</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingInspections.map((inspection) => (
                <tr key={inspection.id}>
                  <td>{inspection.propertyName}</td>
                  <td>{inspection.address}</td>
                  <td>{inspection.requestDate}</td>
                  <td>{getPriorityBadge(inspection.priority)}</td>
                  <td>{getStatusBadge(inspection.status)}</td>
                  <td>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => navigate(`/inspector-dashboard/inspection/${inspection.id}`)}
                    >
                      <i className="bi bi-eye me-1"></i>
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>

      {/* Quick Actions */}
      <Row className="mt-4">
        <Col md="4" className="mb-3">
          <Card className="h-100 hover-shadow" style={{ cursor: 'pointer' }} onClick={() => navigate('/inspector-dashboard/pending-inspections')}>
            <CardBody className="text-center">
              <i className="bi bi-clipboard-check text-primary" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3">View Pending</h5>
              <p className="text-muted">See all properties awaiting inspection</p>
            </CardBody>
          </Card>
        </Col>
        <Col md="4" className="mb-3">
          <Card className="h-100 hover-shadow" style={{ cursor: 'pointer' }} onClick={() => navigate('/inspector-dashboard/inspection-schedule')}>
            <CardBody className="text-center">
              <i className="bi bi-calendar3 text-success" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3">My Schedule</h5>
              <p className="text-muted">View and manage inspection schedule</p>
            </CardBody>
          </Card>
        </Col>
        <Col md="4" className="mb-3">
          <Card className="h-100 hover-shadow" style={{ cursor: 'pointer' }} onClick={() => navigate('/inspector-dashboard/submit-report')}>
            <CardBody className="text-center">
              <i className="bi bi-file-earmark-text text-info" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3">Submit Report</h5>
              <p className="text-muted">Submit a new inspection report</p>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default InspectorDashboard;
